import React from 'react';
import { 
  Font,
  Link,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import _ from 'lodash';

import { LogoViewType } from './types';
import formatImageSource from '../utils/formatImageSource';
import getLogoDimensionsForView from '../utils/getLogoDimensionsForView';
import radiusToText from '../utils/radiusToText';
import removeHyphenation from '../utils/removeHyphenation';

import { RaceEthnicityType, ThemeDataType } from '../../../_libs/data/types';
import { OrganizationBrandData, ReportDataType } from '../../../_libs/app/types';
import PdfImageFromAzureWithOptions from '../../../atoms/PdfImageFromAzureWithOptions';
import PdfImageFromAzureWithUri from '../../../atoms/PdfImageFromAzureWithUri';
import QrCodeSvg from '../../../molecules/QrCodeSvg';

removeHyphenation(Font);

interface LinkedInType {
  isCoBranded: boolean;
  coBrand: OrganizationBrandData;
  lbBrand: OrganizationBrandData;
  imgSrc: string;
  primaryBrand: OrganizationBrandData;
  reportData: ReportDataType;
  themeData: ThemeDataType;
  raceEthnicity: RaceEthnicityType;
  radius: number;
  zipCode: string;
  uuid: string;
}

const LinkedIn = ({
  isCoBranded,
  coBrand,
  lbBrand,
  imgSrc,
  primaryBrand,
  themeData,
  radius,
  zipCode,
  uuid,
}: LinkedInType): JSX.Element => {
  const primaryLogoView: LogoViewType = {
    width: 280,
    height: 65.5,
    position: 'absolute',
    left: 537.5,
    top: 36,
  };
  const primaryLogoDimensions =
    getLogoDimensionsForView(
      {
        width: primaryBrand.logoWidth,
        height: primaryBrand.logoHeight,
      },
      {
        width: primaryLogoView.width,
        height: primaryLogoView.height,
      },
    );
  const coBrandLogoView: LogoViewType = {
    width: 113,
    height: 65.5,
    position: 'absolute',
    left: 872,
    top: 36,
  };
  const coBrandLogoDimensions =
    getLogoDimensionsForView(
      {
        width: coBrand.logoWidth,
        height: coBrand.logoHeight,
      },{
        width: coBrandLogoView.width,
        height: coBrandLogoView.height,
      }
    );
  const lbLogoView: LogoViewType = {
    width: 113,
    height: 65.5,
    position: 'absolute',
    left: 1039,
    top: 36,
  };
  const lbLogoDimensions =
    getLogoDimensionsForView(
      {
        width: 500,
        height: 125,
      },{
        width: lbLogoView.width,
        height: lbLogoView.height,
      }
    );

  const Styles = StyleSheet.create({
    columnLeft: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: 627,
      width: 510,
      backgroundColor: primaryBrand.colors.dark,
      color: 'white',
    },
    announcementView: {
      position: 'absolute',
      left: 36,
      top: 48,
      width: 448,
      height: 132,
      fontFamily: 'Courier',
      fontSize: '16pt',
      textTransform: 'uppercase',
    },
    announcementTheme: {
      fontFamily: 'Times-Roman',
      fontSize: '42pt',
      textTransform: 'capitalize',
    },
    areaView: {
      position: 'absolute',
      left: 36,
      top: 533,
      width: 448,
      height: 65.5,
      fontFamily: 'Helvetica',
      fontSize: '24pt',
      lineHeight: 1.17,
      color: 'white',
    },
    areaEmphasis: {
      fontFamily: 'Times-Bold',
      fontSize: '28pt'
    },
    imageFit: {
      maxWidth: '100%',
      maxHeight: '100%',
    },
    imageFill: {
      minWidth: '100%',
      minHeight: '100%',
    },
    materialIdView: {
      // width: 24,
      // height: 580,
      position: 'absolute',
      bottom: 0,
      right: -3,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
    },
    materialId: {
      width: 580,
      height: 24,
      transform: 'rotate(-90deg)',
      transformOriginX: 580,
      transformOriginY: 24,
      fontFamily: 'Courier',
      fontSize: '12pt',
      color: 'black',
      opacity: 0.5,
    },
    page: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    photo: {
      position: 'absolute',
      left: 263,
      top: 125.5,
      width: 220.5,
      height: 220.5,
      borderRadius: 110.25,
      overflow: 'hidden',
    },
    portrait: {
      position: 'absolute',
      left: 36,
      top: 204,
      width: 280.25,
      height: 280.25,
      borderRadius: 140,
      overflow: 'hidden',
      backgroundColor: primaryBrand.colors.primary,
    },
    primaryLogoView: primaryLogoView,
    primaryLogo: {
      ...primaryLogoDimensions,
      position: 'absolute',
      bottom: 0,
    },
    coBrandLogoView: coBrandLogoView,
    coBrandLogo: {
      ...coBrandLogoDimensions,
      position: 'absolute',
      bottom: 0,
    },
    lbLogoView: lbLogoView,
    lbLogo: {
      ...lbLogoDimensions,
      position: 'absolute',
      bottom: 0,
    },
    qr: {
      width: 145,
      height: 145,
      position: 'absolute',
      top: 356,
      left: 304,
      backgroundColor: 'white',
      transform: 'rotate(-5deg)',
    },
    qrBg: {
      width: 215,
      height: 202.5,
      position: 'absolute',
      left: 272.5,
      top: 325.5,
      transform: 'rotate(-5deg)',
    },
    textView: {
      width: 614.5,
      height: 376,
      position: 'absolute',
      top: 125.5,
      left: 537.5,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      fontFamily: 'Times-Roman',
      fontSize: 42,
      lineHeight: 1,
    },
    textHeader: {
      fontFamily: 'Times-Bold',
      paddingBottom: '22pt',
    },
    textBody: {},
    textBodyEmphasis: {
      fontFamily: 'Times-Italic',
    },
    textCta: {
      width: 614.5,
      height: 65.5,
      position: 'absolute',
      left: 537,
      top: 533,
      fontFamily: 'Helvetica',
      fontSize: '24pt',
      lineHeight: 1.17,
      color: primaryBrand.colors.dark,
    },
    textCtaLink: {
      color: primaryBrand.colors.dark,
    },
  });

  // Debug
  // _.each(Styles, (style) => {
  //   style['border'] = '1px solid magenta';
  // });


  return (
    // 1200px x 627px is recommended by LinkedIn
    primaryLogoDimensions &&
      <Page size={[1200, 627]}>
        <View style={Styles.page}></View>
        <View>
          <View style={Styles.columnLeft}>
            <View style={Styles.announcementView}>
              <Text>An announcement about</Text>
              <Text style={Styles.announcementTheme}>
                {themeData.public_name}
              </Text>
            </View>
            <View style={Styles.areaView}>
              <Text>
                For anyone living within
                &#10;<Text style={Styles.areaEmphasis}>{radiusToText(radius)}</Text> of ZIP code <Text style={Styles.areaEmphasis}>{zipCode}</Text>
              </Text>
            </View>
            <View style={Styles.photo}>
              <PdfImageFromAzureWithOptions
                style={Styles.imageFill}
                fileName={themeData.image_src}
                folderPath="assets/theme_images/"
                />
            </View>
            <View style={Styles.qrBg}>
              <QrCodeSvg scale={1.46} color={primaryBrand.colors.primary} />
            </View>
            <View style={Styles.portrait}>
              <PdfImageFromAzureWithOptions
                style={Styles.imageFill}
                fileName={imgSrc}
                folderPath="assets/people_portraits/"
                />
            </View>
            <View style={Styles.qr}>
              <PdfImageFromAzureWithOptions
                style={Styles.imageFill}
                fileName={themeData.qr_code_src}
                folderPath="assets/theme_qrs/"
              />
            </View>
          </View>
          <View>
            <View style={Styles.primaryLogoView}>
              <PdfImageFromAzureWithUri
                style={Styles.primaryLogo}
                fileUri={primaryBrand.logoUrl}
              />
            </View>
            {
              isCoBranded && coBrand &&
                <>
                  <View style={Styles.coBrandLogoView}>
                    <PdfImageFromAzureWithUri
                      style={Styles.coBrandLogo}
                      fileUri={formatImageSource(coBrand.logoUrl)}
                    />
                  </View>
                  <View style={Styles.lbLogoView}>
                    <PdfImageFromAzureWithUri
                      style={Styles.lbLogo}
                      fileUri={formatImageSource(lbBrand.logoUrl)}
                    />
                  </View>
                </>
            }
            <View style={Styles.textView}>
              <Text style={Styles.textHeader}>
                {themeData.header}
              </Text>
              <Text>
                <Text style={Styles.textBodyEmphasis}>
                  {themeData.body_emphasis}
                </Text>
                &nbsp;{themeData.body}
              </Text>
            </View>
            <View style={Styles.textCta}>
              <Text>
                {themeData.cta} Scan the QR or visit&nbsp;<Link src={`https://${themeData.cta_link}`}><Text>{themeData.cta_link}</Text></Link>.
              </Text>
            </View>
            <View style={Styles.materialIdView}>
              <View style={Styles.materialId}>
                <Text>
                  {/* {materialId()} */}
                  {uuid}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
  );
};

export default LinkedIn;
